import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { LayoutData, PageContent } from '@/types/content'

import { Mail, MapPin } from 'lucide-react'
import { cms } from '@/lib/cms'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/site/page-hero'
import { SocialLinks } from '@/components/site/social-links'
import { ContactForm } from '@/components/site/contact-form'
import { getArtist, getLayout, getPage } from '@/lib/server/content/site'
import { queryParam, setPageCache } from '@/lib/server/page'

interface ContactPageProps {
    layout: LayoutData
    page: PageContent | null
    location: string | null
    initialType: string
}

const ContactPage: FC<ContactPageProps> = ({ layout, page, location, initialType }): ReactNode => {
    const { artist } = layout

    return (
        <Layout layout={layout}>
            <Seo
                title={page?.seo_title ?? 'Contact'}
                description={page?.seo_description}
                ogImage={page?.og_image ?? layout.settings.og_image}
                ogImageAlt={page?.og_image_alt}
                origin={layout.origin}
                path='/contact'
            />

            <PageHero page={page} fallback={{ eyebrow: 'Contact', heading: 'Write to Serene Corpse' }} phase={0.12} />

            <section id='contact-form' aria-label='Contact form' className='shell pb-28'>
                <div className='grid gap-14 lg:grid-cols-12 lg:gap-20'>
                    <div className='lg:col-span-7'>
                        <ContactForm initialType={initialType} />
                    </div>

                    <aside className='lg:col-span-5' aria-label='Other ways to connect'>
                        <div className='rounded-2xl border border-white/[0.07] bg-charcoal/50 p-8 lg:sticky lg:top-28'>
                            <p className='font-heading text-3xl font-light leading-snug'>
                                Collaborations, licensing, media and the conversations in between.
                            </p>

                            <ul className='mt-8 space-y-4 text-sm'>
                                {artist.contact_email && (
                                    <li className='flex items-start gap-3'>
                                        <Mail className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                        <a href={`mailto:${artist.contact_email}`} className='text-ivory/80 transition hover:text-gold'>
                                            <span {...cms('artist', artist.id, 'contact_email')}>{artist.contact_email}</span>
                                        </a>
                                    </li>
                                )}

                                {location && (
                                    <li className='flex items-start gap-3'>
                                        <MapPin className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                        <span className='text-ivory/80'>
                                            <span {...cms('artist', artist.id, 'location')}>{location}</span>
                                        </span>
                                    </li>
                                )}

                                <li className='text-muted-foreground'>
                                    Messages sent here go straight to the artist, and replies arrive at the email address you provide.
                                </li>
                            </ul>

                            <SocialLinks socials={artist} showLabels className='mt-8' />

                            {artist.signature && (
                                <p className='mt-10 font-script text-4xl text-gold/80'>
                                    <span {...cms('artist', artist.id, 'signature')}>{artist.signature}</span>
                                </p>
                            )}
                        </div>
                    </aside>
                </div>
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<ContactPageProps> = async ctx => {
    const [layout, page, artist] = await Promise.all([getLayout(ctx.req), getPage('contact'), getArtist()])

    setPageCache(ctx)

    return { props: { layout, page, location: artist.location, initialType: queryParam(ctx.query.type) } }
}

export default ContactPage
